import { useState } from "react";
import { type OtpHandle, begin, complete } from "../../auth/onboard.ts";
import { signIn } from "../../auth/session.ts";
import { SIGNING_CONFIGURED, useSession } from "../../auth/useAuth.ts";
import { Button } from "../../ui/Button.tsx";
import { Dialog } from "../../ui/Dialog.tsx";
import { Input } from "../../ui/Input.tsx";
import { availableWallets, connectWallet } from "../../wallet/active.ts";
import { turnkeySigner, walletSigner } from "../../wallet/signer.ts";
import type { WalletHandle } from "../../wallet/wallets.ts";
import s from "./auth.module.css";

/**
 * SIGN IN — and, since 2026-08-14, SIGN UP.
 *
 * ── TWO PATHS, AND THE DIALOG PICKS ONE FROM WHAT THE BROWSER ACTUALLY HAS ────────────────────────────
 *
 * RETURNING (a wallet record AND a live signing session): challenge → sign → bearer. ONE signature, no vendor
 * round trip on our side — which is what makes it degrade gracefully: an already-provisioned user can obtain a
 * session **even while the signing vendor's API is down**, because the proof is a signature the browser makes
 * and a verification we perform locally.
 *
 * NEW, or a browser whose signing session has ended: email → code → wallet → signing session, then straight
 * into the returning path. `auth/onboard.ts` is the sequence; this file is only its form.
 *
 * ── THE STATE IS DERIVED, NEVER STORED ────────────────────────────────────────────────────────────────
 *
 * Which path a user is on is READ from `session.signer` (a real keystore probe) and the wallet record, on
 * every render. A `mode` flag set at open time would be stale the moment either changed — and the change that
 * matters most, a signing session expiring, is invisible and silent. `signer === null` (not yet probed) is
 * deliberately NOT ready: offering one-click sign-in that then fails at the signature is the exact failure the
 * probe exists to prevent.
 */
type Step = { k: "idle" } | { k: "sent"; handle: OtpHandle };

export function SignInDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const session = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<Step>({ k: "idle" });
  // ⚠️ RE-READ, NOT SNAPSHOTTED. Extensions register on their own schedule, so the set that has announced
  // itself when this dialog first renders is not the set a second later. Asking on every render costs a
  // map over a handful of objects and means a wallet that woke up late is still offered.
  const wallets: WalletHandle[] = SIGNING_CONFIGURED ? availableWallets() : [];

  const hasWallet = session.address !== null && session.subOrgId !== null;
  const canSignIn = hasWallet && session.signer === true && SIGNING_CONFIGURED;

  /** One place errors become strings, so no branch below can forget to surface a failure. */
  const run = (fn: () => Promise<unknown>): void => {
    if (busy) return;
    setBusy(true);
    setError(null);
    fn()
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(false));
  };

  const doSignIn = (): void => {
    if (!canSignIn || session.address === null || session.subOrgId === null) return;
    run(() =>
      signIn(turnkeySigner(session.subOrgId as string, session.address as string)).then(onClose),
    );
  };

  /**
   * ⚠️ CONNECT THEN SIGN IN, AND THE SECOND STEP IS THE SAME ONE EVERY OTHER SIGNER TAKES. The server's
   * challenge does not care which key answers it — it verifies a signature against an address — so an
   * external wallet needs no second session mechanism, and building one would be two implementations of
   * "how a session is obtained" waiting to drift.
   */
  const doConnect = (handle: WalletHandle): void => {
    run(() =>
      connectWallet(handle)
        .then((address) => signIn(walletSigner(handle, address)))
        .then(onClose),
    );
  };

  const doSendCode = (): void => {
    run(() => begin(email.trim()).then((handle) => setStep({ k: "sent", handle })));
  };

  const doVerify = (): void => {
    if (step.k !== "sent") return;
    run(() =>
      // Onboarding ends with a stored signing session and a wallet record; sign-in is then the SAME path a
      // returning user takes. Calling it here rather than minting a bearer inline is what keeps ONE
      // implementation of "how a session is obtained" — the two would otherwise drift.
      complete(step.handle, code.trim())
        .then((r) => signIn(turnkeySigner(r.subOrgId, r.address)))
        .then(onClose),
    );
  };

  const reset = (): void => {
    setStep({ k: "idle" });
    setCode("");
    setError(null);
  };

  return (
    <Dialog open={open} title="Sign in" onClose={onClose}>
      {!SIGNING_CONFIGURED ? (
        <div className={s.block}>
          <p className={s.warn}>
            Signing is not configured in this build, so no wallet can be reached from this page.
          </p>
        </div>
      ) : canSignIn ? (
        <div className={s.block}>
          <p className={s.muted}>
            Signing in asks your wallet for one signature. <strong>It cannot move funds</strong> —
            it only proves you control this address.
          </p>
          <p className={s.mono}>{session.address}</p>
          <Button variant="primary" state={busy ? "busy" : "idle"} onClick={doSignIn}>
            Sign in
          </Button>
        </div>
      ) : session.signer === null && hasWallet ? (
        // The probe has not answered yet. Showing the email form here would invite a user to re-create an
        // account they already have, one second before the button they actually want appears.
        <div className={s.block}>
          <p className={s.muted}>Checking your signing session…</p>
        </div>
      ) : step.k === "idle" ? (
        <div className={s.block}>
          <p className={s.muted}>
            {hasWallet
              ? "This browser can no longer sign for your wallet. Enter your email to restore access — it is the same wallet, not a new one."
              : "Enter your email and we will send you a code. Your wallet is created on first sign-in and only you can sign for it."}
          </p>
          <Input
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            // ENTER SUBMITS. A dialog whose primary action is unreachable from the keyboard is one a user
            // fights, and this one is on the money path.
            onKeyDown={(e) => {
              if (e.key === "Enter" && email.trim().length > 0) doSendCode();
            }}
          />
          <Button
            variant="primary"
            state={busy ? "busy" : email.trim().length === 0 ? "disabled" : "idle"}
            onClick={doSendCode}
          >
            Send code
          </Button>
        </div>
      ) : (
        <div className={s.block}>
          <p className={s.muted}>
            We sent a code to <strong>{step.handle.email}</strong>. It expires in a few minutes.
          </p>
          <Input
            // `one-time-code` is what lets a phone offer the code from the notification. Worth one attribute.
            autoComplete="one-time-code"
            inputMode="numeric"
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && code.trim().length > 0) doVerify();
            }}
          />
          <div className={s.row}>
            <Button
              variant="primary"
              state={busy ? "busy" : code.trim().length === 0 ? "disabled" : "idle"}
              onClick={doVerify}
            >
              Verify &amp; continue
            </Button>
            {/* A way BACK. Without it, a typo in the email is a dead end that needs a page reload. */}
            <Button variant="ghost" onClick={reset} state={busy ? "disabled" : "idle"}>
              Use a different email
            </Button>
          </div>
        </div>
      )}
      {/*
        ⚠️ OFFERED ALONGSIDE, NOT INSTEAD. A returning user with a Kinetiq wallet should not have to read
        past a wallet list to reach the button they came for — so this sits below whichever path is shown,
        and only while there is actually something to connect. An empty list renders nothing at all rather
        than a heading over a gap, because "no wallets" and "we could not find yours" look the same in an
        empty box and only one of them is worth saying.
      */}
      {SIGNING_CONFIGURED && wallets.length > 0 ? (
        <div className={s.block}>
          <p className={s.muted}>
            Or sign in with a wallet you already have. It asks for one signature and{" "}
            <strong>cannot move funds</strong>.
          </p>
          <div className={s.row}>
            {wallets.map((handle) => (
              <Button
                key={handle.name}
                variant="ghost"
                state={busy ? "disabled" : "idle"}
                onClick={() => doConnect(handle)}
              >
                {handle.name}
              </Button>
            ))}
          </div>
        </div>
      ) : null}
      {error !== null ? <p className={s.warn}>{error}</p> : null}
    </Dialog>
  );
}
